import { Marker } from 'mapbox-gl';
import { Feature, FeatureCollection, LineString, MultiPolygon, point, Properties } from '@turf/helpers';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';

import { AirTable } from '@/models/air-table.model';
import length from '@turf/length';
import midpoint from '@turf/midpoint';
import destination from '@turf/destination';
import bearing from '@turf/bearing';
import lineArc from '@turf/line-arc';
import distance from '@turf/distance';
import { MapStore } from '@/store/map';

const projection = require('@turf/projection');

export const addCityLabel = (mapStore: MapStore, city: any) => {
  mapStore.addSource({
    source: {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: city.coordinates
        },
        properties: {
          fid: 5,
          insee: '92014',
          nom: city.name,
          wikipedia: `fr:${city.name}`,
          class: 'settlement',
          disputed: 'false',
          name: city.name,
          type: 'town',
          worldview: 'all',
          symbolrank: 1,
          filterrank: 1
        }
      }
    },
    type: city.type
  });

  mapStore.addLayer({
    layer: {
      id: 'states',
      source: city.type,
      type: 'symbol',
      layout: {
        'text-field': ['get', 'name']
      },
      paint: {
        'text-color': '#00228c',
        'text-halo-color': '#00228c'
      },
      minzoom: 3,
      maxzoom: city.name === 'Bagneux' ? 12.1 : 13
    },
    type: city.type
  });
};

export const setVisibilityIsMarkerInPolygon = (
  marker: Marker,
  polygon: FeatureCollection<MultiPolygon>,
  layers?: any
) => {
  const { lng, lat } = marker.getLngLat();
  const cord = point([lng, lat]);
  const isInside = booleanPointInPolygon(cord, polygon.features[0]);

  if (isInside) {
    marker.getElement().style.visibility = 'visible';
  } else {
    marker.getElement().style.visibility = 'hidden';
  }
};

export const pointsToGeoJson = <T extends AirTable>(data: T[]): FeatureCollection<GeoJSON.Geometry, Properties> => {
  const features: Feature<GeoJSON.Geometry, Properties>[] = data
    .map(({ image, ...rest }) => ({ ...rest, ...(image && { image: image[0].url }) }))
    .map(({ id, lng, lat, ...content }: any) => ({
      type: 'Feature',
      properties: {
        id,
        content
      },
      geometry: {
        type: 'Point',
        coordinates: [lng, lat]
      }
    }));

  return {
    features,
    type: 'FeatureCollection'
  };
};

export const getCurvedLine = (start: number[], end: number[]) => {
  let route: LineString = {
    type: 'LineString',
    coordinates: [start, end]
  };

  route = projection.toWgs84(route);

  const coordinates = route.coordinates;
  if (end[0] > start[0]) {
    coordinates.reverse();
  }

  const lineD = length(route as any, { units: 'kilometers' });
  const mp = midpoint(route.coordinates[0], route.coordinates[1]);
  const center = destination(mp as any, lineD, bearing(coordinates[0], coordinates[1]) - 90);
  const lA = lineArc(
    center,
    distance(center, route.coordinates[0]),
    bearing(center, coordinates[1]),
    bearing(center, coordinates[0]),
    { steps: 100 }
  );

  return projection.toMercator(lA);
};
